import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Users, Search, Trash2, Shield, ShieldCheck, Loader2, UserCog, Mail, Ship, RefreshCw, AlertTriangle, CheckSquare, Square, Home, Anchor, Key, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/** Global admin-only page: list all users in the system + delete orphaned / unwanted accounts.
 *  Uses GET /api/admin/users and DELETE /api/admin/users/{user_id}.
 *  Guards on the backend: cannot delete yourself, last admin, or sole owner of a boat/household.
 */
export default function AdminUsers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterKind, setFilterKind] = useState("all"); // all | local | external | admin | orphan
  const [deleting, setDeleting] = useState(null); // user_id being deleted
  const [confirm, setConfirm] = useState(null); // row pending confirmation
  const [selected, setSelected] = useState(new Set()); // Set<user_id> for bulk
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  // Edition access management
  const [editionDialog, setEditionDialog] = useState(null); // row being edited
  const [editionDraft, setEditionDraft] = useState([]); // ["boat","household"]
  const [editionSaving, setEditionSaving] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showRequests, setShowRequests] = useState(false);
  const [requestLoading, setRequestLoading] = useState(null); // req_id being decided

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listAdminUsers();
      setRows(res.data.users || []);
      // Also fetch pending requests count for banner
      try {
        const rr = await api.listEditionRequests("pending");
        setPendingRequests(rr.data.requests || []);
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke hente brukere");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openEditionDialog = (row) => {
    const current = row.editions_allowed && row.editions_allowed.length ? row.editions_allowed : ["boat"];
    setEditionDraft([...current]);
    setEditionDialog(row);
  };
  const toggleEditionDraft = (edition) => {
    setEditionDraft((prev) => prev.includes(edition) ? prev.filter((e) => e !== edition) : [...prev, edition]);
  };
  const saveEditions = async () => {
    if (!editionDialog || editionDraft.length === 0) return;
    setEditionSaving(true);
    try {
      const res = await api.setUserEditions(editionDialog.user_id, editionDraft);
      setRows((prev) => prev.map((r) => r.user_id === editionDialog.user_id
        ? { ...r, editions_allowed: res.data.editions_allowed, preferred_edition: res.data.preferred_edition }
        : r));
      toast.success(`Tilganger oppdatert for ${editionDialog.name || editionDialog.email}`);
      setEditionDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setEditionSaving(false);
    }
  };

  const decideRequest = async (req, decision) => {
    setRequestLoading(req.id);
    try {
      await api.decideEditionRequest(req.id, decision);
      toast.success(decision === "approve" ? `Tilgang godkjent for ${req.user_name || req.user_email}` : "Forespørsel avvist");
      setPendingRequests((prev) => prev.filter((r) => r.id !== req.id));
      // Refresh user list so editions column updates
      if (decision === "approve") await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke behandle");
    } finally {
      setRequestLoading(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterKind === "local" && !r.is_boat_local) return false;
      if (filterKind === "external" && r.is_boat_local) return false;
      if (filterKind === "admin" && r.role !== "admin") return false;
      if (filterKind === "orphan" && (r.boat_count || 0) > 0) return false;
      if (!q) return true;
      return [r.email, r.username, r.name, r.user_id].some((x) => (x || "").toLowerCase().includes(q));
    });
  }, [rows, query, filterKind]);

  const doDelete = async (row) => {
    setDeleting(row.user_id);
    try {
      const res = await api.deleteAdminUser(row.user_id);
      const counts = res.data?.counts || {};
      const cleaned = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
      toast.success(`${row.name || row.email} slettet · ${cleaned} relaterte rader fjernet`);
      setConfirm(null);
      setRows((prev) => prev.filter((x) => x.user_id !== row.user_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke slette bruker");
    } finally {
      setDeleting(null);
    }
  };

  const counts = useMemo(() => ({
    total: rows.length,
    admins: rows.filter((r) => r.role === "admin").length,
    local: rows.filter((r) => r.is_boat_local).length,
    orphan: rows.filter((r) => (r.boat_count || 0) === 0).length,
  }), [rows]);

  // Selectable rows = filtered results that are NOT the current admin themselves.
  const selectableIds = useMemo(
    () => filtered.filter((r) => r.user_id !== user?.user_id).map((r) => r.user_id),
    [filtered, user],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id)) && !allSelected;

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) {
        // Deselect only visible ones (keep selection from other filters if any)
        const next = new Set(prev);
        selectableIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      selectableIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const selectOrphansVisible = () => {
    const orphanIds = filtered
      .filter((r) => r.user_id !== user?.user_id && (r.boat_count || 0) === 0 && r.role !== "admin")
      .map((r) => r.user_id);
    setSelected(new Set(orphanIds));
  };

  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.bulkDeleteAdminUsers(ids);
      const { deleted_count, skipped_count, skipped = [] } = res.data || {};
      setRows((prev) => prev.filter((r) => !selected.has(r.user_id) || skipped.some((s) => s.user_id === r.user_id)));
      setSelected(new Set());
      setBulkConfirm(false);
      if (skipped_count > 0) {
        toast.success(`${deleted_count} slettet, ${skipped_count} hoppet over (se konsoll)`);
        console.info("Bulk delete skipped:", skipped);
      } else {
        toast.success(`${deleted_count} brukere slettet permanent`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke slette");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-navy-800 to-navy-700 text-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <Users className="inline h-7 w-7 mr-2 -mt-1" /> Admin – Brukere
            </h1>
            <p className="text-sm opacity-80">Slå opp, moderer og slett brukerkontoer globalt.</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 text-xs font-semibold" data-testid="refresh-users-btn">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Oppdater
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          {[
            { label: "Totalt", value: counts.total, k: "all" },
            { label: "Admins", value: counts.admins, k: "admin" },
            { label: "Båt-lokale", value: counts.local, k: "local" },
            { label: "Uten båt/hus", value: counts.orphan, k: "orphan" },
          ].map((s) => (
            <button
              key={s.k}
              onClick={() => setFilterKind(s.k === filterKind ? "all" : s.k)}
              className={`text-left rounded-xl px-3 py-2 backdrop-blur-sm transition-all ${filterKind === s.k ? "bg-white text-navy-800 ring-2 ring-white" : "bg-white/15 hover:bg-white/25"}`}
              data-testid={`stat-filter-${s.k}`}
            >
              <div className="text-[10px] uppercase tracking-wider opacity-70">{s.label}</div>
              <div className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.value}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Pending edition requests banner */}
      {pendingRequests.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="edition-requests-banner">
          <Clock className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900">
              {pendingRequests.length} forespørsel{pendingRequests.length === 1 ? "" : "er"} om utvidet tilgang
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              Brukere ber om tilgang til en ekstra side (Båt eller Home). Godkjenn eller avvis under.
            </div>
          </div>
          <button
            onClick={() => setShowRequests((s) => !s)}
            className="inline-flex items-center gap-1 rounded-full bg-amber-700 text-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-800"
            data-testid="toggle-requests-btn"
          >
            {showRequests ? "Skjul" : "Vis alle"}
          </button>
        </div>
      )}
      {pendingRequests.length > 0 && showRequests && (
        <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100" data-testid="edition-requests-list">
          {pendingRequests.map((req) => (
            <div key={req.id} className="flex items-center justify-between gap-3 px-4 py-3 flex-wrap" data-testid={`request-${req.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-stone-800 truncate">{req.user_name || req.user_email}</div>
                <div className="text-xs text-stone-500 inline-flex items-center gap-1.5 mt-0.5">
                  Ønsker tilgang til
                  <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 rounded-full px-2 py-0.5 text-[10px] font-semibold">
                    {req.edition === "household" ? <><Home className="h-3 w-3" /> Home Edition</> : <><Anchor className="h-3 w-3" /> Båt</>}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => decideRequest(req, "reject")}
                  disabled={requestLoading === req.id}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-md px-3 py-1.5 disabled:opacity-40"
                  data-testid={`reject-request-${req.id}`}
                >
                  Avvis
                </button>
                <button
                  onClick={() => decideRequest(req, "approve")}
                  disabled={requestLoading === req.id}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-3 py-1.5 disabled:opacity-40"
                  data-testid={`approve-request-${req.id}`}
                >
                  {requestLoading === req.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                  Godkjenn
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <input
          type="text"
          placeholder="Søk e-post, navn eller brukernavn..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-stone-200 bg-white text-sm outline-none focus:border-seawater-500"
          data-testid="search-users-input"
        />
      </div>

      {/* Bulk toolbar — only visible when something is selected, OR offer quick "select orphans" action */}
      <div className="flex flex-wrap items-center gap-2" data-testid="bulk-toolbar">
        <button
          onClick={selectOrphansVisible}
          className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100"
          data-testid="select-orphans-btn"
          title="Velg alle synlige brukere som ikke tilhører noen båt/husstand"
        >
          <CheckSquare className="h-3.5 w-3.5" /> Velg alle foreldreløse
        </button>
        {selected.size > 0 && (
          <>
            <span className="text-xs text-stone-500 font-medium" data-testid="selected-count">
              {selected.size} valgt
            </span>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs font-semibold text-stone-500 hover:text-stone-700 underline"
              data-testid="clear-selection-btn"
            >
              Nullstill
            </button>
            <button
              onClick={() => setBulkConfirm(true)}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold rounded-md px-3 py-1.5 bg-rose-600 text-white hover:bg-rose-700 shadow-sm"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="h-3.5 w-3.5" /> Slett {selected.size} bruker{selected.size === 1 ? "" : "e"}
            </button>
          </>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-stone-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Laster brukere...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-stone-400 py-16 bg-white border border-stone-100 rounded-xl">
          Ingen brukere matcher filteret.
        </div>
      ) : (
        <div className="bg-white border border-stone-100 rounded-xl overflow-hidden">
          {/* Table header (desktop) */}
          <div className="hidden sm:grid grid-cols-[auto_1fr_1fr_auto_auto_auto_auto_auto] gap-4 px-4 py-2 bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 font-semibold border-b border-stone-100">
            <button
              onClick={toggleAll}
              className="flex items-center justify-center hover:text-stone-700"
              aria-label="Velg alle"
              data-testid="toggle-all-btn"
            >
              {allSelected ? <CheckSquare className="h-4 w-4 text-rose-600" /> : someSelected ? <CheckSquare className="h-4 w-4 text-stone-400" /> : <Square className="h-4 w-4" />}
            </button>
            <div>Navn / E-post</div>
            <div>Brukernavn</div>
            <div>Rolle</div>
            <div>Type</div>
            <div>Tilganger</div>
            <div>Båt/Hus</div>
            <div></div>
          </div>
          <div className="divide-y divide-stone-100">
            {filtered.map((row) => {
              const self = row.user_id === user?.user_id;
              const isAdmin = row.role === "admin";
              const isSelected = selected.has(row.user_id);
              return (
                <div
                  key={row.user_id}
                  className={`grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_1fr_auto_auto_auto_auto_auto] gap-2 sm:gap-4 px-4 py-3 transition-colors text-sm ${isSelected ? "bg-rose-50/60" : "hover:bg-sailcloth-100/20"}`}
                  data-testid={`user-row-${row.user_id}`}
                >
                  {/* Checkbox */}
                  <button
                    onClick={() => !self && toggleRow(row.user_id)}
                    disabled={self}
                    className={`flex items-center justify-center self-start mt-0.5 ${self ? "opacity-20 cursor-not-allowed" : "hover:text-rose-700 text-stone-400"}`}
                    aria-label="Velg"
                    data-testid={`select-user-${row.user_id}`}
                  >
                    {isSelected ? <CheckSquare className="h-4 w-4 text-rose-600" /> : <Square className="h-4 w-4" />}
                  </button>
                  <div className="flex items-center gap-2 min-w-0">
                    {row.picture ? (
                      <img src={row.picture} alt="" className="h-7 w-7 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-sailcloth-100 text-navy-800 flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                        {(row.name || row.email || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-medium text-stone-800 truncate">{row.name || "(uten navn)"}</div>
                      <div className="text-xs text-stone-500 truncate flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {row.email || "—"}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-stone-500 truncate sm:pt-1">{row.username || "—"}</div>
                  <div className="sm:pt-1">
                    {isAdmin ? (
                      <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-rose-200">
                        <ShieldCheck className="h-3 w-3" /> Admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-stone-50 text-stone-600 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-stone-200">
                        <Shield className="h-3 w-3" /> Bruker
                      </span>
                    )}
                  </div>
                  <div className="sm:pt-1">
                    {row.is_boat_local ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wider bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full border border-sky-100">
                        Båt-lokal
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-100">
                        Ekstern
                      </span>
                    )}
                  </div>
                  <div className="sm:pt-1">
                    <button
                      onClick={() => openEditionDialog(row)}
                      className="inline-flex items-center gap-1 rounded-full bg-stone-50 border border-stone-200 hover:border-navy-300 hover:bg-sailcloth-100 px-2 py-0.5 text-[10px] font-semibold text-stone-700 transition-colors"
                      data-testid={`editions-btn-${row.user_id}`}
                      title="Rediger tilganger"
                    >
                      {(row.editions_allowed || []).includes("boat") && <Anchor className="h-3 w-3 text-navy-700" />}
                      {(row.editions_allowed || []).includes("household") && <Home className="h-3 w-3 text-amber-700" />}
                      {(row.editions_allowed || []).length === 0 && <span className="text-stone-400 italic">Ingen</span>}
                      <Key className="h-2.5 w-2.5 opacity-50" />
                    </button>
                  </div>
                  <div className="sm:pt-1 text-xs text-stone-500 inline-flex items-center gap-1">
                    <Ship className="h-3 w-3" /> {row.boat_count || 0}
                  </div>
                  <div className="flex items-center gap-1.5 sm:justify-end">
                    {self ? (
                      <span className="text-[10px] text-stone-400 italic inline-flex items-center gap-1">
                        <UserCog className="h-3 w-3" /> Deg selv
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirm(row)}
                        disabled={deleting === row.user_id}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-md px-2 py-1 transition-colors disabled:opacity-40"
                        data-testid={`delete-user-${row.user_id}`}
                      >
                        {deleting === row.user_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                        Slett
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Edition access dialog */}
      <Dialog open={!!editionDialog} onOpenChange={(o) => { if (!o) setEditionDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="editions-dialog">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <Key className="h-5 w-5 text-navy-700" /> Tilganger – {editionDialog?.name || editionDialog?.email}
            </DialogTitle>
            <DialogDescription>
              Velg hvilke sider brukeren skal kunne logge inn på. Minst én må være valgt.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: "boat", label: "Båt", icon: Anchor, color: "navy", desc: "Skift, turer, maritim UI" },
              { key: "household", label: "Home Edition", icon: Home, color: "amber", desc: "Hjem, husstand, budsjett" },
            ].map(({ key, label, icon: Icon, color, desc }) => {
              const active = editionDraft.includes(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleEditionDraft(key)}
                  className={`rounded-xl border-2 p-4 text-left transition-all ${active ? (color === "navy" ? "border-navy-600 bg-sailcloth-100 ring-2 ring-navy-300" : "border-amber-500 bg-amber-50 ring-2 ring-amber-200") : "border-stone-200 bg-white hover:border-stone-300"}`}
                  data-testid={`edition-toggle-${key}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <Icon className={`h-6 w-6 ${active ? (color === "navy" ? "text-navy-700" : "text-amber-700") : "text-stone-400"}`} />
                    {active && <CheckSquare className={`h-4 w-4 ${color === "navy" ? "text-navy-700" : "text-amber-700"}`} />}
                  </div>
                  <div className={`font-semibold text-sm ${active ? "text-stone-900" : "text-stone-600"}`}>{label}</div>
                  <div className="text-[10px] text-stone-500 mt-0.5">{desc}</div>
                </button>
              );
            })}
          </div>
          {editionDraft.length === 0 && (
            <p className="text-xs text-rose-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Velg minst én edition.
            </p>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => setEditionDialog(null)} className="px-4 py-2 rounded-md text-sm font-medium bg-stone-100 hover:bg-stone-200 text-stone-700" data-testid="editions-cancel-btn">
              Avbryt
            </button>
            <button
              onClick={saveEditions}
              disabled={editionSaving || editionDraft.length === 0}
              className="px-4 py-2 rounded-md text-sm font-bold bg-navy-800 hover:bg-navy-900 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="editions-save-btn"
            >
              {editionSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Lagre tilganger
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk confirmation dialog */}
      <Dialog open={bulkConfirm} onOpenChange={(o) => !o && !bulkLoading && setBulkConfirm(false)}>
        <DialogContent className="max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <AlertTriangle className="h-5 w-5 text-rose-600" /> Slett {selected.size} brukere?
            </DialogTitle>
            <DialogDescription>
              Alle deres oppskrifter, handlelister, matplaner og varsler slettes også.
              Brukere som eier båter eller er siste admin blir <span className="font-semibold">hoppet over</span>.
              Denne handlingen <span className="font-bold text-rose-700">kan ikke angres</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-40 overflow-y-auto text-xs text-stone-600 bg-stone-50 rounded-md p-2 space-y-0.5">
            {Array.from(selected).slice(0, 20).map((id) => {
              const r = rows.find((x) => x.user_id === id);
              return (
                <div key={id} className="truncate">
                  • {r?.name || r?.email || id}
                </div>
              );
            })}
            {selected.size > 20 && <div className="italic text-stone-400">…og {selected.size - 20} til</div>}
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setBulkConfirm(false)} disabled={bulkLoading} className="px-4 py-2 rounded-md text-sm font-medium bg-stone-100 hover:bg-stone-200 text-stone-700 disabled:opacity-40" data-testid="bulk-cancel-btn">
              Avbryt
            </button>
            <button
              onClick={doBulkDelete}
              disabled={bulkLoading}
              className="px-4 py-2 rounded-md text-sm font-bold bg-rose-600 hover:bg-rose-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="bulk-confirm-btn"
            >
              {bulkLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Slett {selected.size} permanent
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <AlertTriangle className="h-5 w-5 text-rose-600" /> Slett bruker permanent?
            </DialogTitle>
            <DialogDescription>
              Du er i ferd med å slette <span className="font-semibold text-stone-900">{confirm?.name || confirm?.email}</span> og alle tilhørende data:
              oppskrifter, beholdning, handlelister, matplaner, turer, meldinger og varsler.
              Denne handlingen kan <span className="font-bold text-rose-700">ikke angres</span>.
            </DialogDescription>
          </DialogHeader>
          {confirm && (confirm.boat_count || 0) > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
              <AlertTriangle className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
              Bruker er medlem av {confirm.boat_count} båt(er)/husstand(er). Fjernes fra alle. Eierskap av båter blokkerer sletting – overfør først.
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => setConfirm(null)} className="px-4 py-2 rounded-md text-sm font-medium bg-stone-100 hover:bg-stone-200 text-stone-700" data-testid="cancel-delete-btn">
              Avbryt
            </button>
            <button
              onClick={() => confirm && doDelete(confirm)}
              disabled={deleting === confirm?.user_id}
              className="px-4 py-2 rounded-md text-sm font-bold bg-rose-600 hover:bg-rose-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="confirm-delete-btn"
            >
              {deleting === confirm?.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Slett for alltid
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
